import * as React from "react";
import { Label } from "@radix-ui/react-label";
import styles from "./Textarea.css";

interface TextareaProps {
  name: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  /** Omit it where the label already says what belongs in the field. */
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  maxLength?: number;
  /** Height of the box, in lines. Sized like a comment field by default. */
  rows?: number;
  /**
   * `filled`: a solid box without a border, in body text — e.g. the showroom's
   * comment field. `filled-raised` is the same on a neutral-800 surface, such
   * as a comment card, where `filled` would vanish.
   */
  variant?: "outlined" | "filled" | "filled-raised";
  /** Starts at one line and grows with the text instead of scrolling. */
  autoGrow?: boolean;
  /** For a field without a visible `label`, e.g. one that only has a placeholder. */
  "aria-label"?: string;
  /**
   * Announces the field as required without `required`'s native constraint,
   * which would let the browser's own validation bubble preempt our messages.
   */
  "aria-required"?: boolean;
  /** Id of the element describing this field — normally its error message. */
  "aria-describedby"?: string;
}

/**
 * `TextInput`'s multi-line counterpart, with the same API so the two are
 * interchangeable where a field's shape is only known at runtime.
 */
const Textarea = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  required,
  disabled,
  error,
  maxLength,
  rows = 3,
  variant = "outlined",
  autoGrow = false,
  "aria-label": ariaLabel,
  "aria-required": ariaRequired,
  "aria-describedby": ariaDescribedBy,
}: TextareaProps) => {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  // Resets to `auto` first so the box can shrink again when text is removed.
  // JS rather than `field-sizing: content`, which Firefox doesn't support yet.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!autoGrow || !el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autoGrow, value]);

  return (
    <div className={styles.field}>
      {label && (
        <Label htmlFor={name}>
          {label} {required && "*"}
        </Label>
      )}
      <div className={styles.control({ error, variant })}>
        <textarea
          ref={ref}
          className={styles.textarea({ variant, autoGrow })}
          name={name}
          id={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          rows={autoGrow ? 1 : rows}
          aria-label={ariaLabel}
          aria-invalid={error}
          aria-required={ariaRequired}
          aria-describedby={ariaDescribedBy}
          maxLength={maxLength}
        />
      </div>
    </div>
  );
};

export default Textarea;
